import { Auth } from "src/domains/shared/utils/amplifyCompat";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { SIGNIN_ERROR } from "../constants/constants";
import notice from "../utils/notice";
import useLocale from "./localeHook/useLocale";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import type { SignupOutcome } from "../api/signup/signup.model";
import { confirmSignup, fetchRegistrationStatus, requestSignup } from "../api/signup/signup.repository";
import { signupErrorMessage } from "../utils/signupErrorMessage";
import {
  clearLoginClaimPending,
  markLoginClaimPending,
} from "../utils/exclusiveLogin/loginClaimPending";
import { clearLoginSessionSource } from "../utils/exclusiveLogin/loginSessionSource";
import { LOGIN_RETURN_QUERY, resolveLoginReturnPath } from "../utils/loginReturnPath";
import { LOGIN_CLAIM_FAILURE_MESSAGE_KEY, useLoginClaimFlow } from "./useLoginClaimFlow";
import { useSignOut } from "./useSignOut";

type AuthCondition = "LOGIN" | "LOGOUT";

export interface SignupForm {
  username: string;
  email: string;
  phone: string;
  password1: string;
  password2?: string;
  company: string;
  job: string;
  field: string;
}

export type CognitoUser = {
  attributes: CoginoAttr;
};

export type CoginoAttr = {
  "custom:customAttrs": string;
  email: string;
  email_verified: boolean;
  name: string;
  phone_number: string;
  phone_number_verified: boolean;
  sub: string;
};

export const getUser = async (): Promise<CognitoUser> => {
  const user = await Auth.currentAuthenticatedUser();
  return user as unknown as CognitoUser;
};

/**
 * 로그인·회원가입·로그아웃과 현재 로그인 유저. 회원 탈퇴는 `useWithdrawAccount`(마이페이지 계정 정보)가 든다 —
 * 그 소비자가 이 훅의 상태(마운트마다 GetUser 호출·기기 충돌 창·로케일)를 떠안지 않게 분리했다.
 */
export const useAuth = () => {
  const { t } = useTranslation("common");
  const [locale, setLocale] = useLocale();
  const signOutFlow = useSignOut();

  const [currentUser, setCurrentUser] = useState<CognitoUser>();
  const [emailBeforeConfirm, setEmailBeforeConfirm] = useState<string>("");
  const [authCondition, setAuthCondition] = useState<AuthCondition>("LOGOUT");
  const [error, setError] = useState<any>({});
  const router = useRouter();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  // 사이트 머리말은 확인 전에도 저장된 로그인으로 「로그아웃」 을 보여 확인 중에 로그아웃할 수 있다 — 로그아웃 전에 시작한
  // 확인이 늦게 끝나도 그 결과로 로그인 상태를 되살리지 않게, 로그아웃마다 세어 확인 시작 때와 다르면 버린다.
  const signOutCountRef = useRef(0);

  const getCurrentUser = async () => {
    const signOutCountAtStart = signOutCountRef.current;
    try {
      const user = await getUser();
      if (signOutCountRef.current !== signOutCountAtStart) return;
      if (!user) {
        setIsLoading(true);
      }
      if (user) {
        setIsLoading(false);
        setCurrentUser(user);
      }
    } catch (e) {
      setIsLoading(false);
    }
  };

  /** 세션의 Username(가입 이메일)으로 가입 완료를 다시 부른다 — 결과는 백엔드 판정 그대로이고, IdP 세션 조회의 예외는 호출자가 받는다. */
  const confirmSignupAsSessionUser = async (): Promise<SignupOutcome> => {
    const { username } = await Auth.currentAuthenticatedUser();
    return confirmSignup({ email: username });
  };

  /** 가입 마무리 결과 → 안내 문구. 완료 안내는 토큰 갱신 뒤에 불린다(호출 순서가 불변식). */
  const announceSignupOutcome = (outcome: SignupOutcome) => {
    if (outcome.kind === "done") {
      notice("success", t("myPage.msg.joinCompleted", { ns: "common" }));
    } else if (outcome.kind === "retry") {
      notice("error", t("message.signupRetry", { ns: "auth" }));
    } else {
      notice(
        "error",
        signupErrorMessage(outcome.code, locale.locale) ?? t("message.signUpFailed", { ns: "auth" })
      );
    }
  };

  /**
   * 회복 경로 — 확정은 됐는데 가입 완료가 끝나지 않은 채 떠난 계정(인증코드 모달의 503 뒤 이탈)은 로그인은 되지만 모든
   * 표면에서 미등록이다. 로그인 뒤 등록 여부를 한 번 읽어 미등록이면 "가입 마무리" 를 알리고 `confirm` 을 다시 부른다 —
   * 백엔드는 확정된 계정의 재호출을 등록·그룹 부여로 이어서 끝낸다. 등록을 로그인에서 하는 것이 아니라, 가입 완료 요청을
   * 한 번 더 보내는 것이다(웹 스펙 `signup`). 새 그룹이 보호 화면의 게이트에 보이도록 토큰을 갱신한다.
   */
  const finishSignupIfUnregistered = async () => {
    if ((await fetchRegistrationStatus()) !== "unregistered") return;
    notice("info", t("message.finishingSignup", { ns: "auth" }));
    try {
      const outcome = await confirmSignupAsSessionUser();
      if (outcome.kind === "done") {
        await Auth.refreshSession();
      }
      announceSignupOutcome(outcome);
    } catch {
      // 이 단계의 IdP 예외(세션 조회·토큰 갱신)는 가입 마무리의 실패다 — 로그인은 끝났으므로 이어가고, 다음 로그인이 다시 시도한다.
      notice("error", t("message.signupRetry", { ns: "auth" }));
    }
  };

  // 이동이 끝나야(다음 화면이 그려져야) 끝난다 — 로그인 화면은 그때까지 진행 표시를 유지한다(`signIn`·확인창 `confirm`).
  // 로그인 화면은 방문 기록에서 바꿔 치운다 — 뒤로 가기가 로그인한 사람을 로그인 화면으로 데려가지 않게.
  const completeSignIn = async () => {
    setAuthCondition("LOGIN");
    await getCurrentUser();
    await router.replace(resolveLoginReturnPath(router.query[LOGIN_RETURN_QUERY]));
  };

  // 로그인 직후 점유 — 진행일 때만 로그인을 마친다. 충돌이면 확인창(deviceConflict)을 연다.
  const claimFlow = useLoginClaimFlow({
    onClaimed: async () => {
      clearLoginSessionSource();
      await finishSignupIfUnregistered();
      await completeSignIn();
    },
    onFailed: (reason) =>
      setError({ message: t(LOGIN_CLAIM_FAILURE_MESSAGE_KEY[reason], { ns: "auth" }) }),
    // 취소는 이 브라우저에서만 로그아웃하고 로그인 화면에 머문다 — 따로 할 일이 없다.
    onCancelled: () => {},
  });

  const signIn = async (email: string, password: string): Promise<void> => {
    // 토큰이 저장되기 전에 점유 대기를 시작한다 — 점유 전에 이 로그인으로 다른 요청이 나가지 않게 한다.
    markLoginClaimPending();
    try {
      const result = await Auth.signIn(email, password);
      if (result.isSignedIn) {
        await claimFlow.claim();
      } else {
        // 토큰 없이 끝난 시도(추가 인증 단계)는 점유하지 않고 종전대로 간다.
        clearLoginClaimPending();
        await completeSignIn();
      }
    } catch (error) {
      clearLoginClaimPending();
      const typedErr = error as AxiosError<{ name: string }>;
      const message = SIGNIN_ERROR[typedErr.name as keyof typeof SIGNIN_ERROR];
      if (message) {
        setError({
          message: message.message[locale.locale],
          name: typedErr.name,
        });
        return;
      }
      setError({
        message: locale.locale === "en" ? "Server Error" : "서버 에러",
      });
    }
  };

  /**
   * 거절(4xx)의 `code` 를 `error` 에 둔다 — 인증코드 모달이 `error.name` 으로 코드 불일치 문구를 보인다. 완료·재시도는
   * 지난 거절을 지운다. 화면 전이·안내는 결과를 받은 페이지가 정한다(웹 스펙 `signup`).
   */
  const noteSignupOutcome = (outcome: SignupOutcome) => {
    setError(
      outcome.kind === "refused"
        ? { name: outcome.code ?? undefined, message: signupErrorMessage(outcome.code, locale.locale) }
        : {}
    );
  };

  /** 백엔드 가입 요청 — 완료면 IdP 가 인증코드 메일을 보냈고, 확정은 같은 이메일로 이어진다. */
  const signUp = async (userSignupForm: SignupForm): Promise<SignupOutcome> => {
    const outcome = await requestSignup({
      email: userSignupForm.email,
      password: userSignupForm.password1,
      name: userSignupForm.username,
      phone_number: userSignupForm.phone,
      job: userSignupForm.job,
      company: userSignupForm.company,
      field: userSignupForm.field,
    });
    noteSignupOutcome(outcome);
    if (outcome.kind === "done") {
      setEmailBeforeConfirm(userSignupForm.email);
    }
    return outcome;
  };

  /** 백엔드 가입 완료 — 같은 이메일·코드로 다시 보내도 안전하다(재시도는 호출부가 같은 제출로 한다). */
  const confirmSignUpCode = async (data: { code: string }): Promise<SignupOutcome> => {
    const outcome = await confirmSignup({ email: emailBeforeConfirm, code: data.code });
    noteSignupOutcome(outcome);
    return outcome;
  };

  // 로그아웃 흐름(탭 신호 → 정지 → 자리 해제 → 로컬 로그아웃 → 홈)은 `useSignOut` 이 들고, 이 훅은 그 위에서
  // 자기 로그인 상태를 비운다(로컬 로그아웃 직후·화면 이동 직전). 실패는 `onFailed` 로 받아 error 에 담는다 — 흐름이
  // reject 하지 않으므로 이 signOut 도 reject 하지 않는다.
  const signOut = async () => {
    await signOutFlow({
      onSignedOut: () => {
        signOutCountRef.current += 1;
        setAuthCondition("LOGOUT");
        setCurrentUser(undefined);
        setIsLoading(false);
      },
      onFailed: (error) => {
        console.log(error);
        setError(error);
      },
    });
  };

  useEffect(() => {
    getCurrentUser();
  }, [authCondition]);

  return {
    currentUser,
    confirmSignUpCode,
    signIn,
    signUp,
    signOut,
    error,
    isLoading,
    /** 기기 충돌 확인창 상태와 선택(로그인=다른 기기 밀어내기·취소=이 브라우저 로그아웃) */
    deviceConflict: {
      open: claimFlow.conflictOpen,
      confirming: claimFlow.confirming,
      confirm: claimFlow.confirm,
      cancel: claimFlow.cancel,
    },
  };
};
